import { useEffect, useRef, type ReactNode } from "react";
import { Animated, Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import type { EffortScale } from "@jackedlogs/shared";

import type { UiTheme } from "../theme";
import { MeterRow } from "./MeterRow";

// THE effort meter (AP-0, Decision 8): completing a set IS the effort
// judgment. One full-width easy→hard band — 5 segments on the theme's
// monochrome `effortRamp` — plus a separate Fail button OUTSIDE the
// continuum (failing to move the bar is a different event, not a sixth
// step, so it sits apart with a visible gap, in a NEUTRAL box — not the
// ramp, and not an alarm: a fail is a normal outcome, not an error). One
// tap = the completion gesture; the host
// (logger, AP-0b) owns what the tap triggers (completedAt + PR check +
// rest timer + effort save).
//
// The scale is display-only: segments always REPORT the canonical rir
// (4..0, easy→hard; 4 stands for the "4+" bucket) through onSelect, and
// only the printed numbers flip between RIR (4+ 3 2 1 0) and RPE
// (6 7 8 9 10 = 10 − rir). "Easy" is a legitimate, sometimes deliberate
// choice — the monochrome ramp says intensity, never merit.
//
// Geometry lives in <MeterRow>, not here (MX-5 slice 1): the flexible ≥220×44
// area, the 8pt gap and the fixed right column are that primitive's job, so the
// hold/cardio clock can sit on the identical grid without copying the numbers.
// This file composes it and owns only the CONTENT of the two areas. The right
// column is a named slot (`trailing`) whose default is the Fail button, and the
// axis has one too (`axisTrailing`, default "Hard") — MX-5 turns that column
// into the action column of a timed set, and a default-shaped slot means the
// reps set doesn't move while that happens.
//
// Interaction: no horizontal hitSlop (adjacent segments would overlap),
// so the band is ≥44pt tall outright instead of 36+slop. Selected state
// is never colour-only: an inset ring in the stop's own `on` ink (the
// theme-global ink dies <3:1 on the hard stops of every family — a11y
// audit 2026-07-05) + accessibilityState. Segments are radios, not
// buttons: the band is a mutually-exclusive single-select, and the
// screen reader should say so. Per-stop `on` inks come AA-validated
// from the theme (effortRampContrast test, which also gates ring-vs-fill
// ≥3:1); numbers render solid — the mockup's 0.85 opacity would eat the
// validated margin.
//
// i18n note: this primitive calls t() itself — a documented exception to
// the caller-passes-strings helper contract, because Easy/Hard/Fail are
// intrinsic to the meter's semantics, fixed keys ×5 locales, not caller
// content (same stance as RecentPrsStrip). "Fail" is DELIBERATELY the
// same EN token in all 5 locales (Antenore, 2026-07-05 — like the RIR/RPE
// abbreviations); the localized FailSub underneath carries the semantics
// ("not completed" ×5), so the label is not a missing translation.

// Segment index (0 = easiest) → canonical rir it reports.
const SEGMENT_RIR = [4, 3, 2, 1, 0] as const;
const ALL_STOPS = [0, 1, 2, 3, 4] as const;
// The band's hardest stop — rir 0, "nothing left". The collapsed variant is
// this one stop taking the whole band, which is why it needs no value of its
// own: it reports the same canonical rir the full band's last segment reports.
const HARD_STOP_IDX = 4;
const RIR_LABELS = ["4+", "3", "2", "1", "0"] as const;
// "6−" is the exact twin of RIR's "4+": the easiest segment is a BUCKET (it
// reports rir ≥ 4, i.e. RPE 6 or less), and printing a bare "6" hid that on the
// RPE side alone (MX-5 D1, mockup 2026-07-22).
const RPE_LABELS = ["6−", "7", "8", "9", "10"] as const;

const segmentIndexOf = (rir: number | null): number | null => {
  if (rir === null) return null;
  if (rir >= 4) return 0;
  const idx = SEGMENT_RIR.indexOf(rir as (typeof SEGMENT_RIR)[number]);
  return idx === -1 ? null : idx;
};

export type EffortMeterProps = {
  theme: UiTheme;
  /** The athlete's display scale (Profile.effortScale). Labels only. */
  scale: EffortScale;
  /** Canonical rir of the logged set, null when still pending. ≥4 lights
   *  the "4+" segment. Ignored while `failed`. */
  value: number | null;
  failed: boolean;
  /** Reports the canonical rir (4..0) of the tapped segment. */
  onSelect: (rir: number) => void;
  onFail: () => void;
  disabled?: boolean;
  /** accessibilityHint for the segments — the host says what a tap DOES
   *  ("also logs the set" vs "updates the effort"), which the visual band
   *  can't. i18n-agnostic: the caller passes the localized string. */
  segmentHint?: string;
  /** accessibilityHint for the Fail button, same contract as segmentHint. */
  failHint?: string;
  /** Optional node centered in the axis row, BETWEEN the Easy and Hard labels
   *  (AP-0j: the effort question lives here — "tra easy e hard, in mezzo").
   *  When absent the row stays Easy … Hard. */
  axisCenter?: ReactNode;
  /** Optional node in the axis row's LEFT slot. Defaults to the "Easy"
   *  end-label. The compact (timed/cardio) variant puts the single word
   *  "Effort" here instead: on a hold "how many reps in reserve" has no
   *  answer, so the long question and both end-labels give way (MX-5 D10/Q2 —
   *  direction still reads from the numbers climbing 6− → 10, which is shape,
   *  not colour, so the a11y floor holds). */
  axisLeading?: ReactNode;
  /** Optional node in the axis row's RIGHT slot, over the meter's fixed column.
   *  Defaults to the "Hard" end-label. MX-5 puts the running clock here (the
   *  axis stops naming the ends while a timer owns the row). */
  axisTrailing?: ReactNode;
  /** Optional node in the meter row's fixed right column. Defaults to the Fail
   *  button — ink and a11y unchanged. MX-5 hands this column to the
   *  timed set's action (start chip → elapsed time); when a caller fills it,
   *  Fail is not rendered (D2: on a hold, going to failure IS stop 10, so a
   *  second road to the same datum would be one road too many). */
  trailing?: ReactNode;
  /** Drop the fixed column entirely and give the whole row to the band. Not the
   *  same as passing no `trailing` (which falls back to Fail): this says the row
   *  has no second job right now. A running hold/cardio timer uses it — the band
   *  is then the only control on the row, so it takes every point of width,
   *  which is also what rescues the segments on a narrow screen (DS-27). */
  trailingHidden?: boolean;
  /** Collapse the five stops into ONE wide segment at the hard end, still
   *  reporting the canonical rir 0 (LG-19). For a set the athlete has marked
   *  AMRAP: "how many reps did you have left" has an answer there, and the
   *  answer is already known to be zero — asking it back is a question with one
   *  legal reply, so the scale stops offering five. Same band, same single tap,
   *  same write path (there is no second control anywhere: that is the whole
   *  point). Its sibling case is the compact variant's `axisLeading`, where the
   *  question has NO answer instead of a known one — a plank has no reps.
   *  End-labels give way with it: "Easy … Hard" names the ends of a continuum,
   *  and a one-stop band has none. Fail does NOT: passing this changes the band
   *  only, so "I stopped with nothing left" and "I tried and it didn't move"
   *  stay two different, both-recordable facts (Antenore 2026-07-23). */
  collapsed?: boolean;
  /** The word printed on the collapsed segment, in place of the raw scale
   *  number (LG-19 smoke, Antenore 2026-07-23: "al posto dello 0 metti Done").
   *  "0"/"10" is the RIR/RPE value the tap still commits, but as a label it is
   *  jargon; on a one-stop band the athlete reads a verb, not a number. Only
   *  consulted when `collapsed` — the full ramp always prints its numbers. */
  collapsedLabel?: string;
  /** PR gold pulse (AP-0b): when flashKey increments, the segment matching
   *  flashRir flashes the theme's pr/prFill pair for ~900ms. Transient
   *  celebration only — the durable PR signal stays the row's trophy tag
   *  (gold = status, never part of the ramp). */
  flashRir?: number | null;
  flashKey?: number;
};

export function EffortMeter({
  theme,
  scale,
  value,
  failed,
  onSelect,
  onFail,
  disabled = false,
  segmentHint,
  failHint,
  axisCenter,
  axisLeading,
  axisTrailing,
  trailing,
  trailingHidden = false,
  collapsed = false,
  collapsedLabel,
  flashRir = null,
  flashKey = 0,
}: EffortMeterProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const labels = scale === "rir" ? RIR_LABELS : RPE_LABELS;
  const scaleName = scale === "rir" ? "RIR" : "RPE";
  const selectedIdx = failed ? null : segmentIndexOf(value);
  // The Fail box takes the effort ramp's HARDEST tone (themed, per-family) —
  // a fail is the extreme of effort, not an error, so it's coloured, not grey
  // (Antenore 2026-07-20). Fill + ink ride inline off the last stop, like the
  // segments. Fallback keeps types happy if a theme ever ships an empty ramp.
  const hardStop = theme.effortRamp[theme.effortRamp.length - 1] ?? {
    fill: theme.palette.surface2,
    on: theme.palette.ink,
  };

  // PR pulse — one Animated opacity, re-armed on every flashKey bump so a
  // second PR on the same segment still flashes. Opacity-only → native driver.
  const flashOpacity = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (!flashKey) return;
    flashOpacity.setValue(1);
    Animated.timing(flashOpacity, {
      toValue: 0,
      duration: 900,
      useNativeDriver: true,
    }).start();
  }, [flashKey, flashOpacity]);
  const flashIdx = flashKey ? segmentIndexOf(flashRir) : null;

  return (
    <View>
      <View style={styles.axis}>
        {/* End-labels name the ends of the ramp, so they hold exactly as long
            as the ramp does: collapsed, there is one stop and no "easy" end to
            point at. Slot-passed nodes still win — a caller that fills the slot
            has said what belongs there. */}
        {axisLeading ??
          (collapsed ? null : (
            <Text style={styles.axisLabel}>
              {t("mobile.logger.effortMeterEasy", { defaultValue: "Easy" })}
            </Text>
          ))}
        {/* Center slot (AP-0j) — the effort question sits between Easy and Hard,
            in a flex spacer so the labels stay pinned to the edges even with no
            center node. The compact variant passes a leading node and NO center
            one: there the spacer would be a second flex:1 sibling and would eat
            half the row the word needs for its longest locale, so it collapses
            instead (a11y audit 2026-07-22). */}
        {axisCenter == null && axisLeading != null ? null : (
          <View style={styles.axisCenter}>{axisCenter}</View>
        )}
        {axisTrailing ??
          (collapsed ? null : (
            <Text style={styles.axisLabel}>
              {t("mobile.logger.effortMeterHard", { defaultValue: "Hard" })}
            </Text>
          ))}
      </View>
      <MeterRow
        theme={theme}
        accessibilityRole="radiogroup"
        trailing={
          trailingHidden ? undefined : (
          trailing ?? (
            <Pressable
              accessibilityRole="radio"
              accessibilityLabel={t("mobile.logger.effortMeterFail", {
                defaultValue: "Fail",
              })}
              accessibilityHint={failHint}
              accessibilityState={{
                selected: failed,
                checked: failed,
                disabled,
              }}
              disabled={disabled}
              onPress={onFail}
              style={({ pressed }) => [
                styles.fail,
                { backgroundColor: hardStop.fill },
                failed && styles.failSelected,
                failed && { borderColor: hardStop.on },
                pressed && !disabled && styles.pressed,
                disabled && styles.disabled,
              ]}
            >
              <Text style={[styles.failTitle, { color: hardStop.on }]}>
                {/* ✕ prefix when selected: a SHAPE cue beyond the ring, so
                    selection doesn't read by colour alone. Matches the
                    timeline's ✕. */}
                {failed ? "✕ " : ""}
                {t("mobile.logger.effortMeterFail", { defaultValue: "Fail" })}
              </Text>
            </Pressable>
            )
          )
        }
      >
        <View style={styles.band}>
          {/* One list, two lengths: collapsed renders the hard stop ALONE, and
              it is the very same Pressable — same fill, same validated `on`
              ink, same onSelect(rir), same radio semantics. A second render
              branch here would be a second commit gesture to keep honest. */}
          {(
            (collapsed ? [HARD_STOP_IDX] : ALL_STOPS) as readonly number[]
          ).map((i) => {
            const rir = SEGMENT_RIR[i]!;
            const stop = theme.effortRamp[i];
            if (!stop) return null;
            const selected = selectedIdx === i;
            // The collapsed band prints the caller's verb ("Done") in place of
            // the scale number; the value committed is still rir 0. Everywhere
            // else the number is the label and the a11y name carries the scale.
            const segLabel = collapsed && collapsedLabel ? collapsedLabel : labels[i];
            return (
              <Pressable
                key={rir}
                accessibilityRole="radio"
                accessibilityLabel={
                  collapsed && collapsedLabel
                    ? collapsedLabel
                    : `${scaleName} ${labels[i]}`
                }
                accessibilityHint={segmentHint}
                accessibilityState={{ selected, checked: selected, disabled }}
                disabled={disabled}
                onPress={() => onSelect(rir)}
                style={({ pressed }) => [
                  styles.segment,
                  { backgroundColor: stop.fill },
                  selected && { borderWidth: 2, borderColor: stop.on },
                  pressed && !disabled && styles.pressed,
                  disabled && styles.disabled,
                ]}
              >
                {flashIdx === i ? (
                  <Animated.View
                    pointerEvents="none"
                    style={[styles.prFlash, { opacity: flashOpacity }]}
                  />
                ) : null}
                <Text
                  style={[styles.segmentNum, { color: stop.on }]}
                  numberOfLines={1}
                >
                  {segLabel}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </MeterRow>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    axis: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s2,
      marginBottom: s.s2,
    },
    axisLabel: { color: p.ink3, fontSize: t.sizes.xs },
    // Grows to fill the middle; centers the question between Easy and Hard.
    axisCenter: { flex: 1, alignItems: "center" },
    // Fills MeterRow's leading area, which owns the ≥220×44 floor.
    band: {
      flex: 1,
      flexDirection: "row",
      borderRadius: r.r3,
      overflow: "hidden",
    },
    segment: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
    },
    // Selected ring on the Fail button — a 2px ring whose colour is the ramp
    // stop's own `on` ink (applied inline in the render, so it stays legible on
    // the themed fill), paired with the ✕ glyph so selection reads by weight +
    // shape, not colour. Segments get their ring inline from the stop's `on` ink.
    failSelected: {
      borderWidth: 2,
    },
    segmentNum: {
      fontSize: t.sizes.sm,
      fontWeight: "700",
      fontFamily: t.fonts.mono,
    },
    // PR pulse overlay — BORDER-ONLY in the status gold. A prFill wash was
    // tried first and killed the segment number (stop.on was validated
    // against the ramp fill, not prFill — 1.06:1 on the hard stops, a11y
    // audit AP-0b), so the number's colour never changes; the durable PR
    // signal stays the row's trophy tag.
    prFlash: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderWidth: 3,
      borderColor: p.pr,
    },
    // Fills MeterRow's fixed column (the width + no-shrink live there, in one
    // place, where the axis clock reads the same number).
    // A fail is a normal training outcome, not an error (Antenore 2026-07-20):
    // the box takes the effort ramp's HARDEST tone (themed, per-family, applied
    // inline in the render) — you gave everything, the extreme of effort, never
    // alarm red. The ✕ selected glyph + a 2px ring in the stop's `on` ink carry
    // selection by shape. Narrow: just "Fail" now (the "not completed" sub was
    // dropped — Antenore, it was too wide and pushed the box off the row).
    fail: {
      flex: 1,
      paddingHorizontal: s.s2,
      borderRadius: r.r3,
      alignItems: "center",
      justifyContent: "center",
    },
    failTitle: { fontSize: t.sizes.sm, fontWeight: "700" },
    pressed: { opacity: 0.7 },
    disabled: { opacity: 0.5 },
  });
}
